import { CurrentUser, normalizeCurrentUser } from '../../lib/currentUser'
import { SiteDomain } from '../../lib/portalDomain'
import { hasMatchingPortalAudienceForAudience, hasPortalAccessForDomain } from '../../lib/portalAccess'
import type { AuthCurrentResponse, EntitlementsContext } from '../redux/api/web-api'

type AuthCurrentLike = Partial<AuthCurrentResponse> | void | undefined

export type PortalSessionStatus = 'loading' | 'authenticated' | 'unauthenticated'

export type PortalSessionSnapshot = {
    selectedOrganizationId: string | null
    sessionEntitlements: EntitlementsContext | null | undefined
    sessionPortalAudience: string | null
    sessionUser: CurrentUser | null
    portalAudienceMatches: boolean
    userHasPortalAccess: boolean
    hasUnexpectedEntitlementsContext: boolean
}

// Normalize auth/current payload into one session snapshot the hook can reason about.
export const buildPortalSessionSnapshot = (
    sessionData: AuthCurrentLike,
    {
        currentDomain,
        currentPortalAudience,
    }: {
        currentDomain: SiteDomain | null
        currentPortalAudience: string | null
    }
): PortalSessionSnapshot => {
    const selectedOrganizationId =
        sessionData && 'selectedOrganizationId' in sessionData ? sessionData.selectedOrganizationId ?? null : null
    const sessionEntitlements = sessionData?.entitlements
    const sessionPortalAudience = sessionData?.portalAudience ?? null
    const sessionUser = normalizeCurrentUser(
        sessionData && 'user' in sessionData ? sessionData.user ?? null : null,
        selectedOrganizationId
    )
    const portalAudienceMatches = hasMatchingPortalAudienceForAudience(sessionPortalAudience, currentPortalAudience)
    const userHasPortalAccess = hasPortalAccessForDomain(sessionUser, currentDomain)
    const hasUnexpectedEntitlementsContext =
        currentDomain !== SiteDomain.ADMIN && Boolean(selectedOrganizationId || sessionEntitlements)

    return {
        selectedOrganizationId,
        sessionEntitlements,
        sessionPortalAudience,
        sessionUser,
        portalAudienceMatches,
        userHasPortalAccess,
        hasUnexpectedEntitlementsContext,
    }
}

// Decide whether the hydrated backend session is safe to accept for the current portal.
export const canHydratePortalSession = (
    snapshot: PortalSessionSnapshot,
    hasUnexpectedContextCleanupFailed: boolean
): boolean => snapshot.portalAudienceMatches && snapshot.userHasPortalAccess && !hasUnexpectedContextCleanupFailed

// Collapse RTK query and cleanup state into the public portal-session status.
export const getPortalSessionStatus = ({
    isSuccess,
    isLoading,
    isFetching,
    isClearingUnexpectedContext,
    canHydrateSession,
}: {
    isSuccess: boolean
    isLoading: boolean
    isFetching: boolean
    isClearingUnexpectedContext: boolean
    canHydrateSession: boolean
}): PortalSessionStatus => {
    if (isClearingUnexpectedContext) return 'loading'
    if (isSuccess) {
        return canHydrateSession ? 'authenticated' : 'unauthenticated'
    }
    if (isLoading || isFetching) return 'loading'
    return 'unauthenticated'
}
